import { Form, Input, Popover } from "antd-v5";
import classNames from "classnames";
import _ from "lodash";
import React, { useCallback, useEffect, useState } from "react";
import AIPersonaAPI from "restAPI/AIPersonaAPI";
import { AIPersonaDTO } from "types/api/AIPersonaDTO";

import ACButton from "ac-components/ac-button/ACButton";
import CheckIcon from "ac-components/ac-icons/CheckIcon";
import ChevronDownIcon from "ac-components/ac-icons/ChevronDownIcon";
import EditIcon from "ac-components/ac-icons/EditIcon";
import LoadingIcon from "ac-components/ac-icons/LoadingIcon";
import PlusIcon from "ac-components/ac-icons/PlusIcon";

import "./AIPersona.less";

type CreateAIPersonaDTOType = Omit<AIPersonaDTO, "id"> & { id?: number };
type EditAIPersonaDTOType = AIPersonaDTO | CreateAIPersonaDTOType;

function AIPersonaDetails({
  personas,
  personaSelected,
  onUpdate,
}: {
  personas: AIPersonaDTO[];
  personaSelected?: AIPersonaDTO;
  onUpdate?: (params: AIPersonaDTO) => void;
}) {
  const [editPersona, setEditPersona] = useState<
    EditAIPersonaDTOType | undefined
  >();
  const [tempPersonaSelected, setTempPersonaSelected] = useState<
    AIPersonaDTO | undefined
  >();
  const [form] = Form.useForm();
  const [submissionInprogress, setSubmissionInprogress] =
    useState<boolean>(false);

  const isEditMode = editPersona !== undefined;
  useEffect(() => {
    if (editPersona) {
      form.setFieldsValue(editPersona);
    }
  }, [editPersona, form]);

  const onEdit = useCallback(async () => {
    await form.validateFields();
    try {
      const data = { ...form.getFieldsValue(), isActive: true };
      setSubmissionInprogress(true);
      let response: AIPersonaDTO;
      if (editPersona?.id) {
        response = await AIPersonaAPI.updatePersona(editPersona.id, data)
          .promise;
      } else {
        response = await AIPersonaAPI.createPersona(data).promise;
      }

      if (onUpdate) {
        onUpdate(response);
        setEditPersona(undefined);
      }
    } finally {
      setSubmissionInprogress(false);
    }
  }, [form, editPersona, onUpdate]);

  const onPersonaSelect = (persona: AIPersonaDTO) => {
    const data = { ...persona, isActive: true };
    AIPersonaAPI.updatePersona(persona.id, { isActive: data.isActive });
    onUpdate?.(data);
  };

  const currentPersona = tempPersonaSelected || personaSelected;

  return (
    <div
      className={classNames("ac-ai-persona-details", {
        "edit-persona": isEditMode,
      })}
    >
      <div className="ac-ai-persona-details-section-left">
        <div className="ac-ai-persona-details-section-left-title-wrapper">
          <span className="ac-ai-persona-details-section-left-title">
            Ask as:
          </span>
          <ACButton
            type="secondary"
            iconOnly
            size="sm"
            onClick={() => {
              setEditPersona(
                _.omit(personaSelected, ["id"]) || ({} as AIPersonaDTO),
              );
            }}
            disabled={isEditMode}
          >
            <PlusIcon />
          </ACButton>
        </div>
        <div className="ac-ai-persona-button-wrapper">
          {/* padding in added to avoid clipping of button selection */}
          <div style={{ paddingBottom: 4 }}>
            {personas.map((persona) => {
              const isSelected = personaSelected?.id === persona.id;
              return (
                <ACButton
                  className="ac-ai-persona-button"
                  type={isSelected ? "secondary" : "ghost"}
                  size="sm"
                  disabled={isEditMode}
                  onClick={() => onPersonaSelect(persona)}
                  onMouseEnter={() => setTempPersonaSelected(persona)}
                  onMouseLeave={() => setTempPersonaSelected(undefined)}
                >
                  <span
                    title={persona.name}
                    className="flex-1 text-ellipsis text-left"
                  >
                    {persona.name}
                  </span>
                  {isSelected ? <CheckIcon style={{ fontSize: 20 }} /> : null}
                </ACButton>
              );
            })}
          </div>
        </div>
      </div>
      <div className="ac-ai-persona-details-section-right">
        {!isEditMode ? (
          <>
            <div className="ac-ai-persona-details-section-right-title">
              <span>Prompt</span>
              {!currentPersona?.isDefault ? (
                <ACButton
                  size="sm"
                  type="secondary"
                  onClick={() => {
                    setEditPersona(personaSelected);
                  }}
                >
                  <EditIcon />
                </ACButton>
              ) : null}
            </div>
            <div className="ac-ai-persona-details-section-right-content">
              {currentPersona?.prompt}
            </div>
          </>
        ) : (
          <>
            <div className="ac-ai-persona-details-section-right-form">
              <div className="ac-ai-persona-details-section-right-form-title">
                Tell me about yourself and how you'd like me to reply
              </div>
              <div className="ac-ai-persona-details-section-right-form-fields">
                <Form layout="vertical" form={form} initialValues={editPersona}>
                  <Form.Item
                    name="name"
                    label="Ask as"
                    rules={[
                      { required: true, message: "Field is required" },
                      { whitespace: true },
                      {
                        max: 255,
                        message: "Maximum length is 255 characters",
                      },
                    ]}
                  >
                    <Input />
                  </Form.Item>
                  <Form.Item
                    name="prompt"
                    label="Prompt"
                    rules={[
                      { required: true, message: "Field is required" },
                      { whitespace: true },
                      {
                        max: 1500,
                        message: "Maximum length is 1500 characters",
                      },
                    ]}
                  >
                    <Input.TextArea style={{ height: 100, resize: "none" }} />
                  </Form.Item>
                </Form>
              </div>
              <div className="ac-ai-persona-details-section-right-form-actions">
                <ACButton
                  type="ghost"
                  onClick={() => setEditPersona(undefined)}
                >
                  Cancel
                </ACButton>
                <ACButton onClick={onEdit} loading={submissionInprogress}>
                  Add
                </ACButton>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function AIPersonaPrompt({ persona }: { persona?: AIPersonaDTO }) {
  return (
    <div className="ac-ai-persona-prompt">
      <div className="ac-ai-persona-prompt-title">
        <span>Prompt</span>
      </div>
      <div className="ac-ai-persona-prompt-content">
        {persona?.prompt ?? "No Persona selected for assistance"}
      </div>
    </div>
  );
}

export default function AIPersona({
  disabled,
  defaultPersonaId,
}: {
  disabled?: boolean;
  defaultPersonaId?: number;
}) {
  const [visible, setVisible] = useState(false);
  const [personas, setPersonas] = useState<AIPersonaDTO[]>([]);
  const [loading, setLoading] = useState<boolean>(true);

  useEffect(() => {
    setLoading(true);
    const { promise, cancel } = AIPersonaAPI.getPersonas();
    promise
      .then((personas) => setPersonas(_.sortBy(personas, ["name"])))
      .finally(() => setLoading(false));
    return cancel;
  }, []);

  const personaSelected = personas?.find(({ id, isActive }) =>
    defaultPersonaId ? id === defaultPersonaId : isActive,
  );

  const onUpdate = useCallback((updatedPersona) => {
    setPersonas((prevPersonas) => {
      prevPersonas.forEach((persona) => {
        persona.isActive = false;
      });
      const index = prevPersonas.findIndex(
        (persona) => persona.id === updatedPersona.id,
      );
      if (index === -1) {
        prevPersonas.push(updatedPersona);
      } else {
        prevPersonas[index] = updatedPersona;
      }
      return _.sortBy(prevPersonas, ["name"]);
    });
  }, []);

  return (
    <div className="ac-ai-persona">
      <div className="ac-ai-persona-ask-as-button-wrapper">
        Ask as:
        {loading ? (
          <LoadingIcon />
        ) : (
          <Popover
            rootClassName="ac-ai-persona-details-wrapper"
            content={
              !disabled ? (
                <AIPersonaDetails
                  personas={personas}
                  personaSelected={personaSelected}
                  onUpdate={onUpdate}
                />
              ) : (
                <AIPersonaPrompt persona={personaSelected} />
              )
            }
            trigger={disabled ? "hover" : "click"}
            open={disabled && !personaSelected ? false : visible}
            onOpenChange={setVisible}
            destroyTooltipOnHide
            overlayStyle={{ padding: 0 }}
          >
            <span>
              <ACButton className="p-0" type="ghost" disabled={disabled}>
                <span className="ac-ai-persona-ask-as-button-content">
                  {personaSelected?.name ?? "None"} <ChevronDownIcon />
                </span>
              </ACButton>
            </span>
          </Popover>
        )}
      </div>
    </div>
  );
}
